import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicFormPage() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [optedIn, setOptedIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/forms/${slug}`);
        setForm(data);
        const initial = {};
        data.fields.forEach(f => { initial[f.id] = ''; });
        setFormData(initial);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Form not found or inactive' : 'Failed to load form');
      } finally { setLoading(false); }
    };
    fetchForm();
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const field of form.fields) {
      if (field.required && !formData[field.id]?.trim()) {
        document.getElementById(field.id)?.focus();
        return;
      }
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/public/forms/${slug}/submit`, {
        data: { ...formData, opted_in: optedIn },
      });
      setSubmitted(true);
      setSuccessMsg(data.message);
    } catch (err) {
      alert(err.response?.data?.detail || 'Submission failed.');
    } finally { setSubmitting(false); }
  };

  const updateField = (id, value) => setFormData(prev => ({ ...prev, [id]: value }));

  const brandColor = form?.branding?.primary_color || '#32CD78';
  const brandName = form?.branding?.brand_name || '';
  const logoUrl = form?.branding?.logo_url;

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: brandColor }} />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="text-center max-w-sm">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-gray-800 mb-1">Oops!</h2>
        <p className="text-sm text-gray-500">{error}</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4" data-testid="form-success-page">
      <div className="text-center max-w-sm">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: brandColor + '15' }}>
          <CheckCircle2 className="h-10 w-10" style={{ color: brandColor }} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">You're connected!</h2>
        <p className="text-sm text-gray-500">{successMsg || "Thank you for connecting with us. We'll be in touch soon!"}</p>
        {brandName && <p className="text-xs text-gray-400 mt-4">— {brandName}</p>}
        <p className="text-[10px] text-gray-300 mt-8">Powered by <span className="font-semibold">Konnect</span></p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4" data-testid="public-form-page">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="h-16 w-auto object-contain mx-auto mb-4" />
          ) : brandName ? (
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: brandColor + '15' }}>
              <span className="text-2xl font-bold" style={{ color: brandColor }}>{brandName[0]}</span>
            </div>
          ) : null}
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight">{form.name}</h1>
          <p className="text-sm text-gray-400 mt-2">
            {form.description || 'Connect with us for exclusive promos, freebies, and the latest updates'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {form.fields.map(field => (
            <div key={field.id}>
              <Label htmlFor={field.id} className="text-sm font-medium text-gray-700">
                {field.label} {field.required && <span style={{ color: brandColor }}>*</span>}
              </Label>
              {field.type === 'textarea' ? (
                <Textarea
                  id={field.id}
                  value={formData[field.id] || ''}
                  onChange={e => updateField(field.id, e.target.value)}
                  placeholder={field.placeholder}
                  required={field.required}
                  rows={3}
                  className="mt-1 rounded-xl border-gray-200 focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': brandColor }}
                  data-testid={`public-field-${field.id}`}
                />
              ) : field.type === 'select' ? (
                <Select value={formData[field.id] || ''} onValueChange={v => updateField(field.id, v)}>
                  <SelectTrigger className="mt-1 rounded-xl border-gray-200" data-testid={`public-field-${field.id}`}>
                    <SelectValue placeholder={field.placeholder || 'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(field.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={field.id}
                  type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
                  value={formData[field.id] || ''}
                  onChange={e => updateField(field.id, e.target.value)}
                  placeholder={field.placeholder}
                  required={field.required}
                  className="mt-1 h-12 rounded-xl border-gray-200 text-base focus:ring-2 focus:border-transparent"
                  data-testid={`public-field-${field.id}`}
                />
              )}
            </div>
          ))}

          {/* Opt-in Checkbox */}
          <div className="flex items-start gap-3 py-2" data-testid="opt-in-section">
            <input
              type="checkbox"
              id="opt-in"
              checked={optedIn}
              onChange={e => setOptedIn(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 cursor-pointer"
              style={{ accentColor: brandColor }}
              data-testid="opt-in-checkbox"
            />
            <label htmlFor="opt-in" className="text-xs text-gray-500 cursor-pointer leading-relaxed">
              I agree to receive communications{brandName ? ` from ${brandName}` : ''} including promos, updates, and exclusive offers. You can unsubscribe at any time.
            </label>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 rounded-xl text-base font-semibold text-white shadow-lg transition-all hover:opacity-90 hover:shadow-xl"
            style={{ backgroundColor: brandColor }}
            data-testid="public-form-submit-btn"
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : null}
            {submitting ? 'Connecting...' : form.submit_button_text || 'Connect'}
          </Button>
        </form>

        {/* Consent Text */}
        {brandName && (
          <p className="text-[10px] text-gray-400 text-center mt-4 leading-relaxed">
            By submitting, you agree to connect with <span className="font-medium text-gray-500">{brandName}</span>.
          </p>
        )}

        {/* Powered by */}
        <div className="text-center mt-8 pb-4">
          <p className="text-[10px] text-gray-300">
            Powered by <span className="font-semibold text-gray-400">Konnect</span>
          </p>
        </div>
      </div>
    </div>
  );
}
